import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ModificationRoundQueueItem } from '@/services/dashboard.service';
import type {
  ModificationRoundState,
  ModificationWaitingFor,
} from '@/services/inspection-modification.service';
import {
  OPEN_ROUND_STATES,
  ROUND_STATE_BADGE,
  ROUND_STATE_LABELS,
  WAITING_FOR_LABELS,
} from '@/features/inspection/modification-state-rules';
import { ROUTES } from '@/routes/routes';
import { QueueTableSkeleton } from './QueueTable';

/**
 * D-131 M6 — the Commercial modification-round queue (design §14.2).
 *
 * Replaces the legacy "Returned from Customer" table, which was derived from
 * `customer_approval_status` and could show only "a modification was requested
 * at some point". This queue is sourced from the rounds themselves and shows
 * WHERE each one is and WHO owes the next act.
 *
 * Columns: job · customer · round · state · waiting for · age · assignee. Sorted
 * by age (oldest request first by default) so the round that has waited longest
 * surfaces at the top; filterable by state and by who is waited on. Filtering
 * and sorting are client-side over the server's capped top-N.
 *
 * Nothing here can act on a round: every action lives on the job, behind
 * `inspection:modification:manage`.
 */

const SELECT_CLASS =
  'flex h-9 rounded-lg border border-input bg-card px-2.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1';

const WAITING_FOR_FILTERS: readonly ModificationWaitingFor[] = [
  'COMMERCIAL',
  'OPERATIONS',
  'CUSTOMER',
];

function isRoundState(v: string): v is ModificationRoundState {
  return v in ROUND_STATE_LABELS;
}

function stateLabel(state: string): string {
  return isRoundState(state) ? ROUND_STATE_LABELS[state] : state;
}

function stateBadge(state: string): string {
  return isRoundState(state) ? ROUND_STATE_BADGE[state] : 'bg-muted text-muted-foreground';
}

function waitingLabel(w: string | null): string {
  if (!w) return '—';
  const label = (WAITING_FOR_LABELS as Record<string, string>)[w];
  if (!label) return w;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function ModificationRoundQueueSkeleton() {
  return <QueueTableSkeleton title="Modification Requests" />;
}

interface ModificationRoundQueueProps {
  items: ModificationRoundQueueItem[];
  /** Total open rounds in the tenant; `items` is the server's capped top-N. */
  total: number;
}

export function ModificationRoundQueue({ items, total }: ModificationRoundQueueProps) {
  const navigate = useNavigate();
  const [stateFilter, setStateFilter] = useState<string>('');
  const [waitingFilter, setWaitingFilter] = useState<string>('');
  const [oldestFirst, setOldestFirst] = useState(true);

  const visible = useMemo(() => {
    const filtered = items.filter(
      (r) =>
        (stateFilter === '' || r.state === stateFilter) &&
        (waitingFilter === '' || r.waitingFor === waitingFilter),
    );
    return [...filtered].sort((a, b) =>
      oldestFirst ? b.ageDays - a.ageDays : a.ageDays - b.ageDays,
    );
  }, [items, stateFilter, waitingFilter, oldestFirst]);

  // STAGE C-vi — this queue lists MODIFICATION ROUNDS, so a row opens that
  // round's own workspace rather than the job it belongs to. `roundId` has been
  // on the queue DTO since D-131 M6 (`toRoundQueueItem`), so nothing new is
  // fetched. The job stays one click away from the thread's own context card.
  const open = (r: ModificationRoundQueueItem) =>
    navigate(ROUTES.REQUEST_THREAD.replace(':roundId', r.roundId));

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="modification-round-queue">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3.5">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Modification Requests</h3>
          <span
            className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground"
            title={
              total > items.length
                ? `Showing the ${items.length} oldest of ${total} open requests`
                : undefined
            }
          >
            {total > items.length ? `${items.length} of ${total}` : items.length}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="mod-queue-state">
            Filter by status
          </label>
          <select
            id="mod-queue-state"
            className={SELECT_CLASS}
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            {OPEN_ROUND_STATES.map((s) => (
              <option key={s} value={s}>
                {ROUND_STATE_LABELS[s]}
              </option>
            ))}
          </select>
          <label className="sr-only" htmlFor="mod-queue-waiting">
            Filter by who is waited on
          </label>
          <select
            id="mod-queue-waiting"
            className={SELECT_CLASS}
            value={waitingFilter}
            onChange={(e) => setWaitingFilter(e.target.value)}
          >
            <option value="">Waiting on anyone</option>
            {WAITING_FOR_FILTERS.map((w) => (
              <option key={w} value={w}>
                Waiting on {waitingLabel(w)}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setOldestFirst((v) => !v)}
            className="h-9 rounded-lg border border-input bg-card px-2.5 text-xs text-foreground hover:bg-muted"
            aria-pressed={oldestFirst}
          >
            {oldestFirst ? 'Oldest first' : 'Newest first'}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="px-5 py-6 text-center text-sm text-muted-foreground">
          No open modification requests.
        </p>
      ) : visible.length === 0 ? (
        <p className="px-5 py-6 text-center text-sm text-muted-foreground">
          No open requests match the current filters.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
                <th className="px-5 py-2.5">Job</th>
                <th className="px-5 py-2.5">Customer</th>
                <th className="px-5 py-2.5 hidden sm:table-cell">Request</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5 hidden md:table-cell">Waiting on</th>
                <th className="px-5 py-2.5 text-right">Age</th>
                <th className="px-5 py-2.5 hidden lg:table-cell">Handled by</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visible.map((r) => (
                <tr
                  key={r.roundId}
                  className="cursor-pointer transition-colors hover:bg-muted/30"
                  onClick={() => open(r)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      open(r);
                    }
                  }}
                  tabIndex={0}
                  role="link"
                  aria-label={`Open job ${r.inspectionNumber}, modification request ${r.roundNumber}`}
                >
                  <td className="px-5 py-3 font-medium text-primary">{r.inspectionNumber}</td>
                  <td className="px-5 py-3 text-foreground">
                    {r.customerOrganizationName ?? r.customerName ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground hidden sm:table-cell">
                    #{r.roundNumber}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${stateBadge(r.state)}`}
                    >
                      {stateLabel(r.state)}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-muted-foreground hidden md:table-cell">
                    {waitingLabel(r.waitingFor)}
                  </td>
                  <td className="px-5 py-3 text-right text-muted-foreground">{r.ageDays}d</td>
                  <td className="px-5 py-3 text-muted-foreground hidden lg:table-cell">
                    {r.assigneeName ?? 'Unassigned'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
